import {
    IUsesSummary,
    ISourcesSummary,
    IIncomeSummary,
    IOperatingInfo,
    IPermanentDebtInfo,
    IExpenseSummary,
    IDispositionData,
    IProFormaReturnsData,
    IWaterfallAnalysis,
} from 'views/ProFormaTable/types';
import { WattsType } from 'views/ProFormaTable/constants';
import { IncomeCalculationType } from 'constants/proForma';
import {
    formatDevelopment as baseFormatDevelopment,
    formatAssumptions as baseFormatAssumptions,
    formatForecast as baseFormatForecast,
    formatOperations as baseFormatOperations,
    formatReturns as baseFormatReturns,
} from 'views/ProFormaTable/components/ExecutiveSummary/helpers';
import { Section } from 'views/ProFormaTable/components/ExecutiveSummary/ExecutiveSummaryGenericTable/types';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import {
    InvestmentMemoFormData, TitleData, ExecutiveSummaryData, FieldPath,
} from './types';

export const isEmpty = (value: any) => value === undefined
    || value === null
    || value === ''
    || (Array.isArray(value) && value.length === 0);

// Validates that a numeric string has at most the specified number of decimal places.
export const hasValidDecimalPlaces = (value: string, maxDecimals: number = 2): boolean => {
    if (!value || !value.includes('.')) return true;
    const parts = value.split('.');
    return !parts[1] || parts[1].length <= maxDecimals;
};

export const getEmptyRequiredFields = (formData: InvestmentMemoFormData): Set<FieldPath> => {
    const emptyFields = new Set<FieldPath>();

    // SLIDE 1: Title - all fields required except tagline
    const alwaysRequiredTitleFields: (keyof TitleData)[] = ['dealName', 'sponsor', 'date'];
    const addressTitleFields: (keyof TitleData)[] = ['address', 'city', 'state', 'zipCode'];
    const coordinatesTitleFields: (keyof TitleData)[] = ['latitude', 'longitude'];
    const titleFields: (keyof TitleData)[] = [
        ...alwaysRequiredTitleFields,
        ...(formData.title.useCoordinates ? coordinatesTitleFields : addressTitleFields),
    ];
    titleFields.forEach(field => {
        if (isEmpty(formData.title[field])) {
            emptyFields.add(`title.${field}` as FieldPath);
        }
    });

    // SLIDE 2: Executive Summary - all fields required
    const execSummaryFields: (keyof ExecutiveSummaryData)[] = [
        'opportunityDescription', 'totalCapitalization', 'projectedLpIrr',
        'avgCashOnCash', 'holdPeriod', 'totalEquityRequired',
        'projectedEquityMultiple', 'buildPeriod',
    ];
    execSummaryFields.forEach(field => {
        if (isEmpty(formData.executiveSummary[field])) {
            emptyFields.add(`executiveSummary.${field}` as FieldPath);
        }
    });

    // SLIDE 3: Investment Highlights - need at least one highlight
    if (isEmpty(formData.investmentHighlights.highlights)
        || !formData.investmentHighlights.highlights.some(h => h.isChecked)) {
        emptyFields.add('investmentHighlights.highlights');
    }

    // SLIDE 4: Deal Overview - optional

    // Specs - all fields required, 0 is acceptable for numbers
    const { specs } = formData.dealOverview || {};
    if (isEmpty(specs?.assetType)) {
        emptyFields.add('dealOverview.specs.assetType');
    }

    if (isEmpty(specs?.siteArea)) {
        emptyFields.add('dealOverview.specs.siteArea');
    }
    if (isEmpty(specs?.buildingSizeGrossSf)) {
        emptyFields.add('dealOverview.specs.buildingSizeGrossSf');
    }
    if (isEmpty(specs?.buildingSizeNetRentableSf)) {
        emptyFields.add('dealOverview.specs.buildingSizeNetRentableSf');
    }

    // parkingSpaces - optional per user, don't check

    // unitMixRentRoll - ignore per user, don't check

    // SLIDE 5: Location - need at least one driver and valid coordinates
    if (isEmpty(formData.location.coordinates.lat) || isEmpty(formData.location.coordinates.lng)) {
        emptyFields.add('location.coordinates');
    }
    if (isEmpty(formData.location.drivers) || !formData.location.drivers.some(d => d.isChecked)) {
        emptyFields.add('location.drivers');
    }

    // SLIDE 6: Development Timeline - need at least one milestone
    if (isEmpty(formData.developmentTimeline.milestones)) {
        emptyFields.add('developmentTimeline.milestones');
    }

    // SLIDE 7: Team
    if (isEmpty(formData.team.sponsorCompanyName)) {
        emptyFields.add('team.sponsorCompanyName');
    }

    // assetsUnderManagement - optional per user
    // sponsorBio - optional per user
    // vendors - optional per user

    // SLIDE 8: Market Overview
    // fundamentals - optional per user (AI generated)

    // SLIDES 9-10: Financials - populated from pro forma, no validation needed
    return emptyFields;
};

export const hasEmptyRequiredFields = (formData: InvestmentMemoFormData): boolean => {
    const emptyFields = getEmptyRequiredFields(formData);
    console.log({ emptyFields: Array.from(emptyFields) }); // leave here for now for product
    return emptyFields.size > 0;
};

// Financial helpers
export const roundSectionValues = (sections: Section[], skipRoundingColumns: string[] = []): Section[] => (
    sections.map(section => ({
        ...section,
        rows: section.rows.map(row => ({
            ...row,
            values: row.values.map((value, index) => {
                const columnName = section.columns?.[index];
                if (columnName && skipRoundingColumns.includes(columnName)) {
                    return value;
                }

                if (value.format !== 'currency') {
                    return value;
                }

                if (typeof value.value === 'number') {
                    return {
                        ...value,
                        value: Math.round(value.value),
                    };
                }

                if (typeof value.value === 'string') {
                    if (value.value === '-' || value.value === '') {
                        return value;
                    }

                    const numValue = parseFloat(value.value.replace(/[$,]/g, ''));
                    if (!Number.isNaN(numValue)) {
                        return {
                            ...value,
                            value: numeralFormatterCurrency(Math.round(numValue), '$0,0'),
                        };
                    }
                }

                return value;
            }),
        })),
    }))
);

export const formatDevelopment = (
    proFormaModelId: number,
    usesSummary: IUsesSummary | undefined,
    sourcesSummary: ISourcesSummary | undefined,
): Section[] => {
    const sections = baseFormatDevelopment(proFormaModelId, usesSummary, sourcesSummary);
    return roundSectionValues(sections, ['$ / Built SF']);
};

export const formatAssumptions = (
    proFormaModelId: number,
    incomeSummary: IIncomeSummary,
    operatingInfo: IOperatingInfo,
    expenses: IExpenseSummary,
    wattsType: WattsType,
): Section[] => {
    const sections = baseFormatAssumptions(proFormaModelId, incomeSummary, operatingInfo, expenses, wattsType);
    return roundSectionValues(sections);
};

export const formatForecast = (
    disposition: IDispositionData,
    operatingInfo: IOperatingInfo,
    permanentDebtInfo: IPermanentDebtInfo,
    returnsData: IProFormaReturnsData,
): Section[] => {
    const sections = baseFormatForecast(disposition, operatingInfo, permanentDebtInfo, returnsData);
    return roundSectionValues(sections);
};

export const formatOperations = (
    proFormaModelId: number,
    disposition: IDispositionData,
    incomeCalculationType: IncomeCalculationType,
    wattsType: WattsType,
): Section[] => {
    const sections = baseFormatOperations(proFormaModelId, disposition, incomeCalculationType, wattsType);
    return roundSectionValues(sections);
};

export const formatReturns = (returnsData: IProFormaReturnsData, waterfallAnalysis: IWaterfallAnalysis): Section[] => {
    const sections = baseFormatReturns(returnsData, waterfallAnalysis);
    return roundSectionValues(sections);
};
